import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Stack, Group, Text, Button, Textarea, Badge,
  ThemeIcon, Center, Progress, Divider, Alert, Chip
} from '@mantine/core';
import {
  IconCheck, IconPencil, IconRotateClockwise,
  IconSparkles, IconBrain, IconAlertTriangle, IconUsers
} from '@tabler/icons-react';
import AIGeneratingIcon from '../../components/AIGeneratingIcon';
import { authFetch } from '../../utils/authFetch';

export default function RiskNarrativeCard({
  modelId,
  assessmentUuid,
  bucket,
  path,
  riskFormData,
  onApproved,
  onSwitchToCopilot
}) {
  const [status, setStatus] = useState('queued'); // queued | generating | ready | approved | failed
  const [narrative, setNarrative] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  // Generate narrative (non-streaming, backend returns JSON)
  const generateNarrative = useCallback(async () => {
    try {
      setStatus('generating');
      setKeywords([]);
      setNarrative('');

      // Add debugging logs to verify what we're sending
      console.log('[RiskNarrativeCard] Generating use case analysis with payload:', {
        modelId, 
        assessmentUuid, 
        bucket, 
        path,
        analysisType: 'use_case_analysis'
      });

      const res = await authFetch('/.netlify/functions/generateUseCaseAnalysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          modelId, 
          assessmentUuid, 
          bucket, 
          path
          // Note: generateUseCaseAnalysis doesn't need riskFormData
        })
      });

      if (!res.ok) {
        const errorText = await res.text().catch(() => 'Generation failed');
        throw new Error(errorText || 'Request failed');
      }
      const data = await res.json();
      if (data.success && data.narrative) {
        setNarrative(data.narrative);
        // Generate keywords from risk taxonomy areas and common risk terms
        const extracted = [];
        if (riskFormData) {
          // Extract area names from risk taxonomy
          extracted.push(...Object.keys(riskFormData));
          // Extract source names from each area
          for (const areaData of Object.values(riskFormData)) {
            if (areaData?.riskSources) {
              extracted.push(...areaData.riskSources.map(s => s.name).slice(0, 5));
            }
          }
        }
        const displayKeywords = [...extracted.slice(0, 10), 'limitations', 'bias', 'safety', 'security', 'reliability'].slice(0, 15);
        setKeywords(displayKeywords);
        setStatus('ready');
      } else {
        throw new Error(data.error || 'Narrative generation failed without response');
      }
    } catch (error) {
      console.error('[RiskNarrativeCard] Generation error:', error);
      setStatus('failed');
      setNarrative('');
    }
  }, [modelId, assessmentUuid, bucket, path, riskFormData]);

  useEffect(() => {
    if (status === 'queued' && modelId && assessmentUuid && bucket && path) {
      generateNarrative();
    }
  }, [status, modelId, assessmentUuid, bucket, path, riskFormData, generateNarrative]);

  const handleApprove = () => {
    setStatus('approved');
    onApproved?.(narrative);
  };
  const handleRegenerate = () => { setStatus('queued'); setNarrative(''); };
  const handleRetry = () => setStatus('queued');

  const statusBadge = (() => {
    switch (status) {
      case 'generating':
        return (
          <Badge variant="light" color="blue">
            <Group gap="xs">
              <AIGeneratingIcon isGenerating className="ai-pulse-strong" />
              <Text size="sm">Generating...</Text>
            </Group>
          </Badge>
        );
      case 'ready':
        return <Badge variant="light" color="green">Ready for Review</Badge>;
      case 'approved':
        return (
          <Badge variant="filled" color="green">
            <Group gap="xs">
              <IconCheck size={14} />
              <Text size="sm">Approved</Text>
            </Group>
          </Badge>
        );
      case 'failed':
        return <Badge variant="light" color="red">Generation Failed</Badge>;
      default:
        return <Badge variant="outline" color="gray">Queued</Badge>;
    }
  })();

  const borderColor = ({
    generating: '#1976d2',
    ready: '#059669',
    approved: '#16a34a',
    failed: '#dc2626',
    queued: '#6b7280',
  })[status] || '#6b7280';

  const canEdit = status === 'ready' || status === 'approved';
  const canApprove = status === 'ready' || status === 'approved';
  const progress = status === 'approved' ? 100 : (status === 'ready' ? 100 : (status === 'generating' ? 75 : 25));

  return (
    <Stack gap="lg">
      {/* Header with Progress */}
      <Card withBorder p="lg" bg="gradient-to-r from-blue-50 to-purple-50">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="md" align="center">
                <ThemeIcon size="xl" variant="gradient" gradient={{ from: 'blue', to: 'purple' }}>
                  <IconSparkles size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xl" fw={700}>AI Risk Assessment Context</Text>
                  <Text size="sm" c="dimmed">AI-generated narrative optimized for comprehensive risk analysis</Text>
                </div>
              </Group>
            </div>
            <Badge size="lg" variant="light" color={status === 'approved' ? 'green' : 'blue'}>
              {status === 'approved' ? 'Approved' : 'In Progress'}
            </Badge>
          </Group>

          <Progress value={progress} size="md" radius="xl" color={status === 'approved' ? 'green' : 'blue'} bg="gray.1" />

          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {status === 'approved'
                ? 'Risk context approved - ready for comprehensive risk analysis!'
                : 'Review and approve the AI-generated risk context to proceed'}
            </Text>
            {onSwitchToCopilot && (
              <Button variant="subtle" size="sm" leftSection={<IconUsers size={16} />} onClick={onSwitchToCopilot}>
                Switch to Co-pilot Mode
              </Button>
            )}
          </Group>
        </Stack>
      </Card>

      {/* Main Narrative Card */}
      <Card withBorder radius="md" p="lg" style={{ borderLeftWidth: '4px', borderLeftColor: borderColor, transition: 'all 0.2s ease' }}>
        <Stack gap="md">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <Group gap="sm">
              <ThemeIcon size="md" variant="light" color="#7c3aed"><IconBrain size={20} /></ThemeIcon>
              <div>
                <Text fw={700} size="lg">Risk Assessment Context</Text>
                <Text size="sm" c="dimmed">Comprehensive narrative covering technical limitations, bias profile, safety concerns, and compliance requirements</Text>
              </div>
            </Group>
            {statusBadge}
          </Group>

          {/* Keywords */}
          {keywords.length > 0 && (
            <>
              <Divider />
              <div>
                <Text size="sm" fw={600} mb="xs" c="dimmed">Risk Analysis Keywords Found:</Text>
                <Group gap="xs">
                  {keywords.slice(0, 15).map((keyword, idx) => (
                    <Chip key={idx} size="xs" variant="outline" color="blue" checked={false}>{keyword}</Chip>
                  ))}
                  {keywords.length > 15 && <Text size="xs" c="dimmed">+{keywords.length - 15} more</Text>}
                </Group>
              </div>
            </>
          )}

          {/* Body */}
          <div style={{ minHeight: '200px' }}>
            {status === 'generating' && (
              <Stack gap="md">
                <Center py="md">
                  <Stack gap="sm" align="center">
                    <AIGeneratingIcon isGenerating className="ai-pulse-strong" />
                    <Text size="sm" c="blue">AI is generating comprehensive risk assessment context...</Text>
                  </Stack>
                </Center>
              </Stack>
            )}

            {status === 'queued' && (
              <Center py="xl"><Text size="sm" c="dimmed">Initializing risk assessment context generation...</Text></Center>
            )}

            {(status === 'ready' || status === 'approved') && (
              <>
                {isEditing ? (
                  <Textarea
                    value={narrative}
                    onChange={(e) => setNarrative(e.currentTarget.value)}
                    minRows={12}
                    maxRows={20}
                    autosize
                    placeholder="Edit the risk assessment narrative..."
                    styles={{ input: { fontSize: '14px', lineHeight: 1.6 } }}
                  />
                ) : (
                  <Card withBorder radius="sm" p="md" bg="gray.0">
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '14px' }}>
                      {narrative || 'No narrative generated.'}
                    </Text>
                  </Card>
                )}
              </>
            )}

            {status === 'failed' && (
              <Alert color="red" variant="light" p="md" icon={<IconAlertTriangle />}>
                <Text size="sm">Risk assessment context generation failed. Please try again or check your model documentation.</Text>
              </Alert>
            )}
          </div>

          {/* Actions */}
          <Divider />
          <Group justify="space-between" gap="sm">
            <Group gap="xs">
              <Button size="xs" variant="subtle" leftSection={<IconPencil size={14} />} disabled={!canEdit} onClick={() => setIsEditing(!isEditing)}>
                {isEditing ? 'Done' : 'Edit'}
              </Button>
              <Button size="xs" variant="light" leftSection={<IconRotateClockwise size={14} />} disabled={status === 'generating' || status === 'queued'} onClick={handleRegenerate}>
                Regenerate
              </Button>
            </Group>
            <Group gap="xs">
              {status === 'failed' && <Button size="xs" variant="outline" onClick={handleRetry}>Retry</Button>}
              {canApprove && (
                <Button size="sm" onClick={handleApprove} variant={status === 'approved' ? 'filled' : 'light'} color="green" leftSection={<IconCheck size={16} />}>
                  {status === 'approved' ? 'Approved' : 'Approve'}
                </Button>
              )}
            </Group>
          </Group>
        </Stack>
      </Card>

      {status === 'approved' && (
        <Card withBorder p="lg" bg="green.0">
          <Group justify="center" gap="md">
            <ThemeIcon size="lg" variant="light" color="green"><IconCheck size={20} /></ThemeIcon>
            <div>
              <Text fw={600} c="green">Risk Assessment Context Complete!</Text>
              <Text size="sm" c="dimmed">Your AI will now automatically analyze risks across all relevant areas using this optimized context.</Text>
            </div>
          </Group>
        </Card>
      )}
    </Stack>
  );
}
// // src/autopilotMode/components/RiskNarrativeCard.jsx
// // Full, streaming-capable React component consuming SSE from /.netlify/functions/generateRiskNarrative

// import React, { useEffect, useState, useCallback } from 'react';
// import {
//   Card, Stack, Group, Text, Button, Textarea, Badge,
//   ThemeIcon, Center, Progress, Divider, Alert, Chip
// } from '@mantine/core';
// import {
//   IconCheck, IconPencil, IconRotateClockwise,
//   IconSparkles, IconBrain, IconAlertTriangle, IconUsers
// } from '@tabler/icons-react';
// import AIGeneratingIcon from '../../components/AIGeneratingIcon';

// export default function RiskNarrativeCard({
//   modelId,
//   assessmentUuid,
//   bucket,
//   path,
//   contextForm,
//   onApproved,
//   onSwitchToCopilot
// }) {
//   const [status, setStatus] = useState('queued'); // queued | generating | ready | approved | failed
//   const [narrative, setNarrative] = useState('');
//   const [keywords, setKeywords] = useState([]);
//   const [isEditing, setIsEditing] = useState(false);
//   const [streamContent, setStreamContent] = useState('');
//   const [isStreaming, setIsStreaming] = useState(false);

//   const parseSSEEvent = (raw) => {
//     const lines = raw.split('\n').filter(Boolean);
//     let evt = 'message';
//     let dataLine = null;
//     for (const line of lines) {
//       if (line.startsWith('event:')) evt = line.slice(6).trim();
//       else if (line.startsWith('data:')) dataLine = line.slice(5).trim();
//     }
//     if (!dataLine) return null;
//     try {
//       return { evt, payload: JSON.parse(dataLine) };
//     } catch {
//       return null;
//     }
//   };

//   const generateNarrative = useCallback(async () => {
//     try {
//       setStatus('generating');
//       setKeywords([]);
//       setStreamContent('');
//       setNarrative('');
//       setIsStreaming(true);

//       const res = await authFetch('/.netlify/functions/generateRiskNarrative', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({ modelId, assessmentUuid, bucket, path, contextForm })
//       });

//       if (!res.ok || !res.body) {
//         const errorText = await res.text().catch(() => 'Streaming failed');
//         throw new Error(errorText || 'Stream init failed');
//       }

//       const reader = res.body.getReader();
//       const decoder = new TextDecoder();
//       let buffer = '';
//       let finalNarrative = '';

//       const flushEvents = () => {
//         let idx;
//         while ((idx = buffer.indexOf('\n\n')) !== -1) {
//           const rawEvent = buffer.slice(0, idx);
//           buffer = buffer.slice(idx + 2);
//           const parsed = parseSSEEvent(rawEvent);
//           if (!parsed) continue;
//           const { evt, payload } = parsed;

//           if (evt === 'delta' && payload?.delta) {
//             setStreamContent(prev => prev + payload.delta);
//           } else if (evt === 'done' && payload?.narrative != null) {
//             finalNarrative = String(payload.narrative || '');
//           } else if (evt === 'error') {
//             console.error('[RiskNarrativeCard] SSE error:', payload?.message);
//           }
//         }
//       };

//       while (true) {
//         const { value, done } = await reader.read();
//         if (done) break;
//         buffer += decoder.decode(value, { stream: true });
//         flushEvents();
//       }
//       if (buffer.trim()) flushEvents();

//       setIsStreaming(false);
//       const finalText = (finalNarrative || '').trim() || (streamContent || '').trim();
//       setNarrative(finalText);
//       setStreamContent(finalText);
//       setStatus(finalText ? 'ready' : 'failed');

//       // Keyword chips for display (quick extraction from contextForm)
//       const extracted = [];
//       if (contextForm) {
//         for (const [, sectionDef] of Object.entries(contextForm)) {
//           if (Array.isArray(sectionDef)) {
//             for (const item of sectionDef) {
//               const name = typeof item === 'string' ? item : item?.name;
//               if (name) extracted.push(name);
//             }
//           }
//         }
//       }
//       const displayKeywords = [...extracted.slice(0, 10), 'limitations', 'bias', 'safety', 'security', 'reliability'].slice(0, 15);
//       setKeywords(displayKeywords);
//     } catch (error) {
//       console.error('[RiskNarrativeCard] Generation error:', error);
//       setIsStreaming(false);
//       setStatus('failed');
//       setStreamContent('');
//     }
//   }, [modelId, assessmentUuid, bucket, path, contextForm, streamContent]);

//   useEffect(() => {
//     if (status === 'queued' && modelId && assessmentUuid && bucket && path) {
//       generateNarrative();
//     }
//   }, [status, modelId, assessmentUuid, bucket, path, contextForm, generateNarrative]);

//   const handleApprove = () => {
//     setStatus('approved');
//     onApproved?.(narrative);
//   };

//   //const handleEditToggle = () => setIsEditing(!isEditing);
//   const handleRegenerate = () => { setStatus('queued'); setNarrative(''); setStreamContent(''); };
//   const handleRetry = () => setStatus('queued');

//   const statusBadge = (() => {
//     switch (status) {
//       case 'generating':
//         return (
//           <Badge variant="light" color="blue">
//             <Group gap="xs">
//               <AIGeneratingIcon isGenerating className="ai-pulse-strong" />
//               <Text size="sm">Generating...</Text>
//             </Group>
//           </Badge>
//         );
//       case 'ready':
//         return <Badge variant="light" color="green">Ready for Review</Badge>;
//       case 'approved':
//         return (
//           <Badge variant="filled" color="green">
//             <Group gap="xs">
//               <IconCheck size={14} />
//               <Text size="sm">Approved</Text>
//             </Group>
//           </Badge>
//         );
//       case 'failed':
//         return <Badge variant="light" color="red">Generation Failed</Badge>;
//       default:
//         return <Badge variant="outline" color="gray">Queued</Badge>;
//     }
//   })();

//   const borderColor = ({
//     generating: '#1976d2',
//     ready: '#059669',
//     approved: '#16a34a',
//     failed: '#dc2626',
//     queued: '#6b7280',
//   })[status] || '#6b7280';

//   const canEdit = status === 'ready' || status === 'approved';
//   const canApprove = status === 'ready' || status === 'approved';
//   const progress = status === 'approved' ? 100 : (status === 'ready' ? 100 : (status === 'generating' ? 75 : 25));

//   return (
//     <Stack gap="lg">
//       {/* Header with Progress */}
//       <Card withBorder p="lg" bg="gradient-to-r from-blue-50 to-purple-50">
//         <Stack gap="md">
//           <Group justify="space-between" align="flex-start">
//             <div>
//               <Group gap="md" align="center">
//                 <ThemeIcon size="xl" variant="gradient" gradient={{ from: 'blue', to: 'purple' }}>
//                   <IconSparkles size={24} />
//                 </ThemeIcon>
//                 <div>
//                   <Text size="xl" fw={700}>AI Risk Assessment Context</Text>
//                   <Text size="sm" c="dimmed">AI-generated narrative optimized for comprehensive risk analysis</Text>
//                 </div>
//               </Group>
//             </div>
//             <Badge size="lg" variant="light" color={status === 'approved' ? 'green' : 'blue'}>
//               {status === 'approved' ? 'Approved' : 'In Progress'}
//             </Badge>
//           </Group>

//           <Progress value={progress} size="md" radius="xl" color={status === 'approved' ? 'green' : 'blue'} bg="gray.1" />

//           <Group justify="space-between">
//             <Text size="sm" c="dimmed">
//               {status === 'approved'
//                 ? 'Risk context approved - ready for comprehensive risk analysis!'
//                 : 'Review and approve the AI-generated risk context to proceed'}
//             </Text>
//             {onSwitchToCopilot && (
//               <Button variant="subtle" size="sm" leftSection={<IconUsers size={16} />} onClick={onSwitchToCopilot}>
//                 Switch to Co-pilot Mode
//               </Button>
//             )}
//           </Group>
//         </Stack>
//       </Card>

//       {/* Main Narrative Card */}
//       <Card withBorder radius="md" p="lg" style={{ borderLeftWidth: '4px', borderLeftColor: borderColor, transition: 'all 0.2s ease' }}>
//         <Stack gap="md">
//           {/* Header */}
//           <Group justify="space-between" align="flex-start">
//             <Group gap="sm">
//               <ThemeIcon size="md" variant="light" color="#7c3aed"><IconBrain size={20} /></ThemeIcon>
//               <div>
//                 <Text fw={700} size="lg">Risk Assessment Context</Text>
//                 <Text size="sm" c="dimmed">Comprehensive narrative covering technical limitations, bias profile, safety concerns, and compliance requirements</Text>
//               </div>
//             </Group>
//             {statusBadge}
//           </Group>

//           {/* Keywords */}
//           {keywords.length > 0 && (
//             <>
//               <Divider />
//               <div>
//                 <Text size="sm" fw={600} mb="xs" c="dimmed">Risk Analysis Keywords Found:</Text>
//                 <Group gap="xs">
//                   {keywords.slice(0, 15).map((keyword, idx) => (
//                     <Chip key={idx} size="xs" variant="outline" color="blue" checked={false}>{keyword}</Chip>
//                   ))}
//                   {keywords.length > 15 && <Text size="xs" c="dimmed">+{keywords.length - 15} more</Text>}
//                 </Group>
//               </div>
//             </>
//           )}

//           {/* Body */}
//           <div style={{ minHeight: '200px' }}>
//             {status === 'generating' && (
//               <Stack gap="md">
//                 <Center py="md">
//                   <Stack gap="sm" align="center">
//                     <AIGeneratingIcon isGenerating className="ai-pulse-strong" />
//                     <Text size="sm" c="blue">AI is generating comprehensive risk assessment context...</Text>
//                   </Stack>
//                 </Center>
//                 {streamContent && (
//                   <Card withBorder radius="sm" p="md" bg="blue.0">
//                     <Text size="sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '14px' }}>
//                       {streamContent}{isStreaming && <Text component="span" c="blue">▊</Text>}
//                     </Text>
//                   </Card>
//                 )}
//               </Stack>
//             )}

//             {status === 'queued' && (
//               <Center py="xl"><Text size="sm" c="dimmed">Initializing risk assessment context generation...</Text></Center>
//             )}

//             {(status === 'ready' || status === 'approved') && (
//               <>
//                 {isEditing ? (
//                   <Textarea
//                     value={narrative}
//                     onChange={(e) => setNarrative(e.currentTarget.value)}
//                     minRows={12}
//                     maxRows={20}
//                     autosize
//                     placeholder="Edit the risk assessment narrative..."
//                     styles={{ input: { fontSize: '14px', lineHeight: 1.6 } }}
//                   />
//                 ) : (
//                   <Card withBorder radius="sm" p="md" bg="gray.0">
//                     <Text size="sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '14px' }}>
//                       {narrative || 'No narrative generated.'}
//                     </Text>
//                   </Card>
//                 )}
//               </>
//             )}

//             {status === 'failed' && (
//               <Alert color="red" variant="light" p="md" icon={<IconAlertTriangle />}>
//                 <Text size="sm">Risk assessment context generation failed. Please try again or check your model documentation.</Text>
//               </Alert>
//             )}
//           </div>

//           {/* Actions */}
//           <Divider />
//           <Group justify="space-between" gap="sm">
//             <Group gap="xs">
//               <Button size="xs" variant="subtle" leftSection={<IconPencil size={14} />} disabled={!canEdit} onClick={() => setIsEditing(!isEditing)}>
//                 {isEditing ? 'Done' : 'Edit'}
//               </Button>
//               <Button size="xs" variant="light" leftSection={<IconRotateClockwise size={14} />} disabled={status === 'generating' || status === 'queued'} onClick={handleRegenerate}>
//                 Regenerate
//               </Button>
//             </Group>
//             <Group gap="xs">
//               {status === 'failed' && <Button size="xs" variant="outline" onClick={handleRetry}>Retry</Button>}
//               {canApprove && (
//                 <Button size="sm" onClick={handleApprove} variant={status === 'approved' ? 'filled' : 'light'} color="green" leftSection={<IconCheck size={16} />}>
//                   {status === 'approved' ? 'Approved' : 'Approve'}
//                 </Button>
//               )}
//             </Group>
//           </Group>
//         </Stack>
//       </Card>

//       {status === 'approved' && (
//         <Card withBorder p="lg" bg="green.0">
//           <Group justify="center" gap="md">
//             <ThemeIcon size="lg" variant="light" color="green"><IconCheck size={20} /></ThemeIcon>
//             <div>
//               <Text fw={600} c="green">Risk Assessment Context Complete!</Text>
//               <Text size="sm" c="dimmed">Your AI will now automatically analyze risks across all relevant areas using this optimized context.</Text>
//             </div>
//           </Group>
//         </Card>
//       )}
//     </Stack>
//   );
// }